import React, { Fragment, useState } from 'react'
import { StandardPage } from '../../components/_EOS/StandardPage'
import { ModernLabel } from '../../components/_WMS/ModernLabel'
import {
  Box,
  Button,
  FormControl,
  Grid,
  IconButton,
  LinearProgress,
  Link,
  ListItemText,
  TextField,
  Tooltip,
  Typography
} from '@mui/material'
import { withStyles } from '@mui/styles'
import { ProductOriginsDDL } from '../../components/Producto/ProductOrigins/ProductOriginsDDL'
import { TypeOperationDLL } from '../../components/TypeOperation/TypeOperationDLL'
import { useProductOrigins, useSuppliers } from '../../hooks'
import { WarehouseDDL } from '../../components/Almacen'
import { SuppliersDDL } from '../../components/Supplier/SuppliersDDL'
import { SupplierOriginDDL } from '../../components/SupplierOrigin/SupplierOriginDDL'
import HelpIcon from '@mui/icons-material/Help'
import { getPackingListTemplate, postPackingList } from './api'
import { SnackBar } from '../../components/SnackBar/Snackbar'
import { useSnackbar } from '../../hooks/useSnackbar'
import { useApi } from '../../hooks/useApi'
import { Loading } from '../../components/UI'
import { Status } from '../../hooks/useFetchState'
import { useSuppliersOrigin } from '../../hooks/useSuppliersOrigin'

const BorderLinearProgress = withStyles(() => ({
  root: {
    height: 15,
    borderRadius: 5
  },
  colorPrimary: {
    backgroundColor: '#EEEEEE'
  },
  bar: {
    borderRadius: 5,
    backgroundColor: '#1a90ff'
  }
}))(LinearProgress)

interface PackingListNewPageProps {}

export const PackingListNewPage: React.FC<PackingListNewPageProps> = () => {
  const { isActive, isSuccess, message, openSnackBar } = useSnackbar()
  const [currentFile] = useState<any | undefined>(undefined)
  const [selectedProductOrigins, setSelectedProductOrigins] =
    useState<number>(0)
  const [selectedTypeOperation, setSelectedTypeOperation] = useState<number>(0)
  const [allowedTypeOperationIds, setAllowedTypeOperationIds] = useState<
    number[]
  >([])

  const [selectedWarehouse, setSelectedWarehouse] = useState<number>(-1)
  const [allowedWarehouseIds, setAllowedWarehouseIds] = useState<number[]>([])

  const [selectedSupplier, setSelectedSupplier] = useState<number>(0)
  const [allowedSupplierIds, setAllowedSupplierIds] = useState<number[]>([])

  const [selectedSupplierOrigin, setSelectedSupplierOrigin] =
    useState<number>(0)
  const [allowedSupplierOriginIds, setAllowedSupplierOriginIds] = useState<
    number[]
  >([])

  const [selectedPackageQty, setSelectedPackageQty] = useState<
    number | undefined
  >()
  const [receptionDate, setReceptionDate] = useState<string>('')
  const [comment, setComment] = useState<string>('')
  const [progress, setProgress] = useState<number>(0)
  const [selectedFiles, setSelectedFiles] = useState<any[] | undefined>(
    undefined
  )

  const [errorTypeOperation, setErrorTypeOperation] = useState<string>('')
  const [errorWarehouse, setErrorWarehouse] = useState<string>('')
  const [errorSupplier, setErrorSupplier] = useState<string>('')

  const { state: requestLabelState, callApi: requestDownloadTemplate } =
    useApi()
  const { state: requestPackingListState, callApi: requestCreatePackingList } =
    useApi()

  const {
    productOriginsState: { productOrigins }
  } = useProductOrigins()

  const {
    suppliersState: { suppliers }
  } = useSuppliers()

  const {
    suppliersOriginState: { suppliersOrigin }
  } = useSuppliersOrigin()

  const handleSetProductOrigin = (id: number) => {
    setSelectedProductOrigins(id)
    setSelectedTypeOperation(0)
    setSelectedSupplier(0)
    setSelectedSupplierOrigin(0)
    setSelectedWarehouse(-1)
    setErrorTypeOperation('')
    setErrorWarehouse('')
    setErrorSupplier('')

    const selected = productOrigins?.find((po) => po.productOriginId === id)

    if (selected) {
      // obtiene los typeOperationId relacionados
      const typeOps =
        selected.typeOperation
          ?.filter((f) => f.warehouses && f.warehouses.length > 0)
          .map((wo: any) => wo.typeOperationId) || []

      if (typeOps.length === 0)
        setErrorTypeOperation('Marca no tiene tipos de operación configurados')

      // se setean los tipos de operacion configurados
      setAllowedTypeOperationIds(typeOps)
      // si hay solo uno, queda seleccionado
      if (typeOps.length === 1) {
        handleSetTypeOperation(typeOps[0], id)
      }
    } else {
      setAllowedTypeOperationIds([])
    }
  }

  const handleSetTypeOperation = (id: number, productOriginId?: number) => {
    setSelectedTypeOperation(id)
    setSelectedSupplier(0)
    setSelectedSupplierOrigin(0)
    setSelectedWarehouse(-1)
    setErrorWarehouse('')
    setErrorSupplier('')

    const poId = productOriginId || selectedProductOrigins
    const selected = productOrigins?.find((po) => po.productOriginId === poId)

    if (selected) {
      const typeOp = selected.typeOperation?.find(
        (wo: any) => wo.typeOperationId === id
      )

      const warehouseAlloweds: number[] =
        typeOp?.warehouses?.map((w: any) => w.warehouseId) || []

      if (warehouseAlloweds.length === 0)
        setErrorWarehouse(
          'La marca seleccionada no tiene bodegas configuradas para este flujo'
        )

      setAllowedWarehouseIds(warehouseAlloweds)

      // si hay solo uno, queda seleccionado
      if (warehouseAlloweds.length === 1) {
        handleSetWarehouse(warehouseAlloweds[0], poId)
      }
    } else {
      setAllowedWarehouseIds([])
    }
  }

  const handleSetWarehouse = (id: number, productOriginId?: number) => {
    setSelectedSupplier(0)
    setSelectedSupplierOrigin(0)
    setSelectedWarehouse(id)
    setErrorSupplier('')

    // obtenemos el producto origin seleccionado actualmente
    const poId = productOriginId || selectedProductOrigins

    const suppliersAllowedAux =
      suppliers
        ?.filter(
          (s) => s.warehouseIds.includes(id) && s.productOriginId === poId
        )
        .map((m) => m.supplierId) || []

    if (suppliersAllowedAux.length === 0)
      setErrorSupplier('No hay proveedor configurado, para Marca seleccionada')

    setAllowedSupplierIds(suppliersAllowedAux)
    // Si hay exactamente uno, seleccionarlo automáticamente
    if (suppliersAllowedAux.length === 1) {
      handleSetSupplier(suppliersAllowedAux[0])
    }
  }

  const handleSetSupplier = (id: number) => {
    setSelectedSupplierOrigin(0)
    setSelectedSupplier(id)

    const suppOriginAux =
      suppliersOrigin
        ?.filter((item) => item.active && item.supplierId === id)
        .map((m) => m.id) || []

    setAllowedSupplierOriginIds(suppOriginAux)

    // Si hay exactamente uno, seleccionarlo automáticamente
    if (suppOriginAux.length === 1) {
      handleSetSupplierOrigin(suppOriginAux[0])
    }
  }

  const handleSetSupplierOrigin = (id: number) => {
    setSelectedSupplierOrigin(id)
  }

  const handleDateReceptionChange = (value: string) => {
    setReceptionDate(value) // La fecha se maneja como string en formato YYYY-MM-DD
  }

  const handleSetComment = (value: string) => {
    setComment(value)
  }

  const handleSelectFile = (event: any) => {
    setSelectedFiles(event.target.files)
    setProgress(0)
  }

  const handleSetPackageQty = (qty: number) => {
    setSelectedPackageQty(qty)
  }

  const handleSubmit = () => {
    const formData = new FormData()

    // Agregar los valores del formulario
    formData.append('warehouseId', `${selectedWarehouse}`)
    formData.append('supplierId', `${selectedSupplier}`)
    formData.append('supplierOriginId', `${selectedSupplierOrigin}`)
    formData.append('estimatedDate', receptionDate)
    formData.append('packagesQty', `${selectedPackageQty || 0}`)
    formData.append('comments', `${comment}`)
    formData.append('productOriginId', `${selectedProductOrigins}`)
    formData.append('typeOperationId', `${selectedTypeOperation}`)

    // Agregar el archivo seleccionado
    if (selectedFiles && selectedFiles.length > 0) {
      formData.append('file', selectedFiles[0])
      formData.append('packingListName', selectedFiles[0].name)
    }
    requestCreatePackingList(postPackingList(formData))
      .then(() => {
        openSnackBar('Carga realizada exitosamente', true)
      })
      .catch((error) => {
        openSnackBar(error, false)
      })
  }

  const handleDownloadTemplate = () => {
    requestDownloadTemplate(getPackingListTemplate())
      .then((response) => {
        const raw = window.atob(response.file)
        const uInt8Array = new Uint8Array(raw.length)
        for (let i = 0; i < raw.length; ++i) {
          uInt8Array[i] = raw.charCodeAt(i)
        }
        const link = document.createElement('a')
        const blob = new Blob([uInt8Array], {
          type: response.mimeType
        })
        link.style.display = 'none'
        link.href = URL.createObjectURL(blob)
        link.setAttribute('download', response.name)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      })
      .catch((error) => {
        openSnackBar(error, false)
      })
  }

  const validateButtonLoad = () => {
    if (
      !selectedFiles ||
      selectedProductOrigins <= 0 ||
      selectedTypeOperation <= 0 ||
      selectedWarehouse <= 0 ||
      selectedSupplier <= 0 ||
      selectedSupplierOrigin <= 0
    )
      return true
    else return false
  }

  const isDisableTypeOperation = () => {
    return (
      selectedProductOrigins < 1 ||
      errorTypeOperation.length > 0 ||
      allowedTypeOperationIds.length === 1
    )
  }

  const isDisableWarehouse = () => {
    return (
      selectedTypeOperation < 1 ||
      errorWarehouse.length > 0 ||
      allowedWarehouseIds.length === 1
    )
  }

  const isDisableSupplier = () => {
    return (
      selectedWarehouse < 1 ||
      allowedSupplierIds.length === 1 ||
      errorSupplier.length > 0
    )
  }

  const isDisableSupplierOrigin = () => {
    return selectedSupplier <= 0 || allowedSupplierOriginIds.length === 1
  }

  if (
    requestLabelState.status === Status.Loading ||
    requestPackingListState.status === Status.Loading
  ) {
    return (
      <div>
        <Typography>
          <Loading />
        </Typography>
      </div>
    )
  }

  return (
    <Fragment>
      <StandardPage pageTitle='Gestionar Packing List'>
        <Grid container spacing={2} direction='row'>
          {/* Primera Fila */}
          <Grid item xs={12} sm={6} md={3}>
            <ModernLabel label='Marca'>
              <ProductOriginsDDL
                onChange={(e: any) =>
                  handleSetProductOrigin(e.target.value as number)
                }
                value={selectedProductOrigins}
                type='outlined'
                fullWidth
                error={selectedProductOrigins < 1}
                errorText={
                  selectedProductOrigins < 1
                    ? 'Debe seleccionar primero una marca para continuar.'
                    : undefined
                }
              />
            </ModernLabel>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <ModernLabel label='Tipo de Operación'>
              <TypeOperationDLL
                value={selectedTypeOperation}
                onChange={(e: any) => {
                  handleSetTypeOperation(e.target.value as number)
                }}
                fullWidth={true}
                type='outlined'
                allowedIds={allowedTypeOperationIds}
                disabled={isDisableTypeOperation()}
                error={errorTypeOperation.length > 0}
                errorText={errorTypeOperation}
              />
            </ModernLabel>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <ModernLabel label='Almacén'>
              <WarehouseDDL
                value={selectedWarehouse}
                onChange={(e: any) => {
                  handleSetWarehouse(e.target.value as number)
                }}
                fullWidth={true}
                type='outlined'
                allowedIds={allowedWarehouseIds}
                disabled={isDisableWarehouse()}
                error={errorWarehouse.length > 0}
                errorText={errorWarehouse}
              />
            </ModernLabel>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <ModernLabel label='Proveedor'>
              <SuppliersDDL
                onChange={(e: any) =>
                  handleSetSupplier(e.target.value as number)
                }
                value={selectedSupplier}
                type='outlined'
                fullWidth={false}
                allowedWarehouseIds={[selectedWarehouse]}
                disabled={isDisableSupplier()}
                productOriginId={selectedProductOrigins}
                error={errorSupplier.length > 0}
                errorText={errorSupplier}
              />
            </ModernLabel>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <ModernLabel label='Origen de Proveedor'>
              <SupplierOriginDDL
                onChange={(e: any) =>
                  handleSetSupplierOrigin(e.target.value as number)
                }
                value={selectedSupplierOrigin}
                type='outlined'
                fullWidth={false}
                disabled={isDisableSupplierOrigin()}
                supplierId={selectedSupplier}
              />
            </ModernLabel>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <ModernLabel label='Bultos/ Pallets/ Bin'>
              <TextField
                // autoFocus
                // tabIndex={1}
                type='number'
                variant='outlined'
                fullWidth
                name='packagesQty'
                value={selectedPackageQty}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const value = parseInt(e.target.value, 10)
                  if (value >= 0 || e.target.value === '') {
                    handleSetPackageQty(value)
                  }
                }}
                inputProps={{
                  min: 0,
                  step: 1
                }}
              />
            </ModernLabel>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <ModernLabel label='Fecha Estimada de recepción'>
              <FormControl>
                <TextField
                  name='receptionDate'
                  type='date'
                  value={receptionDate}
                  onChange={(e: any) =>
                    handleDateReceptionChange(e.target.value as string)
                  }
                  variant='outlined'
                />
              </FormControl>
            </ModernLabel>
          </Grid>
          <Grid item xs={12} sm={6} md={6}>
            <ModernLabel label='Observaciones'>
              <TextField
                fullWidth
                name='comment'
                multiline
                rows={5}
                variant='outlined'
                onChange={(e: any) =>
                  handleSetComment(e.target.value as string)
                }
                value={comment}
              />
            </ModernLabel>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Grid
              container
              spacing={1}
              direction='column'
              alignItems='center'
              style={{ marginTop: '10px' }}
            >
              {/* Botón seleccionar archivo */}
              <Grid item>
                <label htmlFor='btn-upload'>
                  <input
                    id='btn-upload'
                    name='btn-upload'
                    style={{ display: 'none' }}
                    type='file'
                    onChange={handleSelectFile}
                    accept='.xls,.xlsx'
                  />
                  <Tooltip
                    title='Seleccionar el archivo Excel a cargar'
                    placement='top'
                  >
                    <Button
                      className='btn-choose'
                      variant='outlined'
                      component='span'
                    >
                      Seleccionar Archivo
                    </Button>
                  </Tooltip>
                </label>
              </Grid>

              {/* Nombre archivo */}
              <Grid item>
                <Box>
                  <Typography variant='subtitle2'>
                    {selectedFiles && selectedFiles.length > 0
                      ? selectedFiles[0].name
                      : 'Ningún Archivo Seleccionado.'}
                  </Typography>
                </Box>
              </Grid>

              {/* Barra de progreso */}
              {currentFile && (
                <Grid item style={{ width: '100%' }}>
                  <BorderLinearProgress
                    variant='determinate'
                    value={progress}
                  />
                  <Box minWidth={35}>
                    <Typography
                      variant='body1'
                      color='textSecondary'
                      style={{ textAlign: 'end' }}
                    >
                      {`${progress}%`}
                    </Typography>
                  </Box>
                </Grid>
              )}

              {/* Template */}
              <Grid item xs={12}>
                <ListItemText
                  primary=''
                  secondary={
                    <Box>
                      <Typography
                        component='ol'
                        style={{ listStyle: 'square' }}
                      >
                        <Typography
                          variant='subtitle2'
                          component='li'
                          gutterBottom
                        >
                          Puede descargar una <b>plantilla</b> desde el link que
                          se encuentra disponible a continuación.
                          <br />
                          <Link
                            style={{ cursor: 'pointer' }}
                            onClick={handleDownloadTemplate}
                          >
                            Descargar Plantilla
                          </Link>
                        </Typography>
                      </Typography>
                    </Box>
                  }
                />
              </Grid>
            </Grid>
          </Grid>
          {/* Fila Botón + Icono (Grid) */}
          <Grid item xs={12}>
            <Box
              display='flex'
              justifyContent='center'
              alignItems='center'
              width='100%'
            >
              <Tooltip title='Enviar el archivo...' placement='top'>
                <Button
                  onClick={handleSubmit}
                  variant='contained'
                  color='primary'
                  style={{ width: 'auto', minWidth: 120 }}
                  size='large'
                  disabled={validateButtonLoad()}
                >
                  Cargar
                </Button>
              </Tooltip>

              <Tooltip
                title='Debe seleccionar los campos obligatorios (Marca, Tipo de Operación, Almacén, Proveedor y su Origen) y cargar el archivo, para continuar'
                placement='top'
              >
                <IconButton>
                  <HelpIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Grid>
        </Grid>
      </StandardPage>
      {/* SNACKBAR */}
      <SnackBar
        message={message}
        open={isActive}
        status={isSuccess ? 'success' : 'error'}
      />
    </Fragment>
  )
}
